import React, { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { compose } from 'utils/compose';
import { useParams } from 'react-router-dom';
import { inject, observer } from 'mobx-react';
import { Grid } from '@mui/material';

import ExitSessionDialog from 'components/Dialog/ExitSession';

import LocationDialog from './Dialog/LocationDialog';
import AccessCodeDialog from './Dialog/AccessCodeDialog';
import PreferPhotoDialog from './Dialog/PreferPhotoDialog';
import InstructionDialog from './Dialog/InstructionDialog';
import AddressDialog from './Dialog/AddressDialog';
import SessionNoticeDialog from './Dialog/SessionNoticeDialog';
import { AddressQuestionnaire, AccessCode, DeliveryInstruction, Location, DeliveryPhoto, DeliveryAddress } from './components';
import { SessionCountdown, Overview, Header, ExpiredToken, Backdrop, Hr } from 'components/DeliveryManager';
import FooterContainer from 'components/Footer/FooterContainer';

import { getCookie } from 'utils/cookie';
import useSearchParams from 'hooks/useSearchParams';
import {
  ACCESS_CODE,
  INVALID_ACCESS_CODE,
  NO_ACCESS_CODE,
  GATED_ADDRESS,
  ADDRESS_NOT_ACCESSIBLE,
  ADDRESS_QUESTIONNAIRE,
  ADDRESS,
  PREFER_POD,
  DELIVERY_PREFER_POD,
  DELIVERY_INSTRUCTION,
  DELIVERY_ADDRESS,
  QUESTIONNAIRE_PROPERTIES,
  ADDRESS_CORRECTED,
  ADDRESS_MISSING_UNIT,
  invalidAddressReasons,
  invalidAddressIncidents,
  INCIDENT_ADDRESS_CORRECTED,
  INCIDENT_ADDRESS_MISSING_UNIT,
  ADDRESS_AUTO_CORRECTED,
  INCIDENT_ADDRESS_AUTO_CORRECTED,
  COMMUNICATION_ISSUE,
  ACCESS_DENIED_BY_DOORMAN,
  BUSINESS_IS_CLOSED,
} from 'constants/common';
import ConfirmCorrectedAddressDialog from './Dialog/ConfirmSuggestedAddressDialog';
import ShowAutoCorrectedAddressDialog from './Dialog/ShowAutoCorrectedAddressDialog';
import WSComponent from './WSComponent';

const PREFIX = 'ManageDelivery';

const classes = {
  root: `${PREFIX}-root`,
  container: `${PREFIX}-container`,
  section: `${PREFIX}-section`,
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled('div')(({ theme }) => ({
  [`& .${classes.root}`]: {
    padding: '1rem',
    maxWidth: '1280px',
    margin: '0 auto',
  },

  [`& .${classes.container}`]: {
    color: theme.colors.common12,
    padding: '1rem 1.5rem',
    [theme.breakpoints.up('sm')]: {
      padding: '1rem 3rem',
    },
    border: `0.3px solid ${theme.colorTokens.outlineVariant}`,
    borderTop: 'none',
    backgroundColor: theme.colorTokens.surfaceContainerLowest,
  },

  [`& .${classes.section}`]: {
    display: 'grid',
    gridAutoFlow: 'row',
    gap: '1rem',
    marginBottom: '1rem',
    [theme.breakpoints.up('sm')]: {
      gridAutoFlow: 'column',
      gridTemplateColumns: '1fr 1fr',
    },
  },
}));

export const ManageDelivery = compose(
  inject('store'),
  observer,
)(({ store }) => {
  const { trackingCode } = useParams();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);

  const { deliveryStore } = store;
  const { delivery, token } = deliveryStore;

  const reason = searchParams.get('reason');
  const skip = searchParams.get('skip_questionnaire');
  const dialogStatus = searchParams.get('dialog');

  const questionnaire = delivery?.['recipient-questionnaire'];
  const completed = typeof questionnaire === 'object' && QUESTIONNAIRE_PROPERTIES.every((property) => questionnaire.hasOwnProperty(property));
  const show = completed || skip === 'skipped';

  const accessCodeReasons = [ACCESS_CODE, INVALID_ACCESS_CODE, NO_ACCESS_CODE, GATED_ADDRESS];
  const addressReasons = [ADDRESS, ADDRESS_NOT_ACCESSIBLE, ADDRESS_QUESTIONNAIRE];
  const preferDropoffPhotoReasons = [PREFER_POD, DELIVERY_PREFER_POD];
  const instructionDialogReasons = [DELIVERY_INSTRUCTION, ACCESS_DENIED_BY_DOORMAN, COMMUNICATION_ISSUE];

  const incidentType = delivery?.incident?.type;

  const renderDialogs = () => {
    if (dialogStatus === 'closed') return;

    if (accessCodeReasons.includes(reason)) return <AccessCodeDialog />;
    if (addressReasons.includes(reason)) return <LocationDialog />;
    if (preferDropoffPhotoReasons.includes(reason)) return <PreferPhotoDialog />;
    if (instructionDialogReasons.includes(reason)) return <InstructionDialog />;
    if (reason === DELIVERY_ADDRESS) return <AddressDialog />;
    if (reason === BUSINESS_IS_CLOSED) return <InstructionDialog />;

    if (delivery?.incident !== undefined) {
      if (invalidAddressReasons.includes(reason) || invalidAddressIncidents.includes(incidentType)) return <AddressDialog />;
      if (reason === ADDRESS_CORRECTED || incidentType === INCIDENT_ADDRESS_CORRECTED) return <ConfirmCorrectedAddressDialog />;
      if (reason === ADDRESS_AUTO_CORRECTED || incidentType === INCIDENT_ADDRESS_AUTO_CORRECTED) return <ShowAutoCorrectedAddressDialog />;
      if (reason === ADDRESS_MISSING_UNIT || incidentType === INCIDENT_ADDRESS_MISSING_UNIT) return <AddressDialog />;
    }
  };

  useEffect(() => {
    setIsLoading(true);
    const token = getCookie('axl_shipment_token');
    if (!token) {
      setIsLoading(false);
      return;
    }

    deliveryStore.getTokenInfo(token, (response) => {
      setIsLoading(false);
      if (response.status !== 200) return;

      deliveryStore.getDelivery(trackingCode, () => {
        deliveryStore.markManageDeliveryOpened();
        if (delivery?.incident?.type !== undefined && reason === undefined) {
          searchParams.set('dialog', 'opened');
        }
      });
      deliveryStore.getAccessCodeTypes();
      deliveryStore.getAddressTypes();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackingCode]);

  if (!token && !isLoading) return <ExpiredToken />;

  if (!delivery) return <Backdrop />;

  return (
    <Root>
      <div className={classes.root}>
        <WSComponent />
        <SessionCountdown />
        <div className={classes.container}>
          <Overview />
          <Hr />
          <Grid>
            <Grid>
              <AddressQuestionnaire />
            </Grid>
            {show && (
              <>
                <Grid>
                  <Header />
                </Grid>
                <div className={classes.section}>
                  <Grid>
                    <DeliveryAddress />
                  </Grid>
                  <Grid>
                    <AccessCode />
                  </Grid>
                </div>
                <Grid>
                  <DeliveryInstruction />
                </Grid>
                <Grid>
                  <Location />
                </Grid>
                <Grid>
                  <DeliveryPhoto />
                </Grid>
              </>
            )}
          </Grid>
        </div>
        {renderDialogs()}
        <ExitSessionDialog />
        <SessionNoticeDialog />
      </div>
      <FooterContainer />
    </Root>
  );
});
